/**
 * ForcePasswordChangeModal — Sprint 33A · Feb 2026.
 *
 * Rendered globally by the Layout when `user.must_change_password === true`.
 * The user cannot dismiss it (no close button, no ESC handler). Only a
 * successful password change removes it from the DOM.
 */
import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CircleNotch, ShieldCheck, LockKey, Warning } from "@phosphor-icons/react";

export default function ForcePasswordChangeModal() {
  const { user, setUser } = useAuth();
  const { lang } = useLang();
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [saving, setSaving] = useState(false);
  const t = (es, en) => (lang === "es" ? es : en);

  if (!user?.must_change_password) return null;

  const submit = async () => {
    if (newPw.length < 8) {
      toast.error(t("La nueva contraseña debe tener al menos 8 caracteres", "Password must be at least 8 chars"));
      return;
    }
    if (newPw !== confirmPw) {
      toast.error(t("Las contraseñas no coinciden", "Passwords don't match"));
      return;
    }
    if (newPw === currentPw) {
      toast.error(t("La nueva contraseña debe ser diferente", "New password must be different"));
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: currentPw, new_password: newPw });
      // Refresh user in context so the flag turns off
      const { data } = await api.get("/auth/me");
      setUser(data);
      localStorage.setItem("sdm_user", JSON.stringify(data));
      toast.success(t("Contraseña actualizada. Bienvenido a SDM Concierge.", "Password updated. Welcome to SDM Concierge."));
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="force-password-change-modal"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="bg-card border-2 border-primary rounded-lg shadow-2xl max-w-md w-full p-6">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck size={22} weight="fill" className="text-primary" />
          <div className="font-heading font-bold text-lg">
            {t("Cambio de contraseña obligatorio", "Password change required")}
          </div>
        </div>
        <div className="text-xs text-muted-foreground mb-4">
          {t(
            "Estás usando la contraseña temporal generada por el sistema. Por seguridad debes crear una nueva antes de continuar.",
            "You are using the system-generated temporary password. For security you must create a new one before continuing.",
          )}
        </div>

        <div className="text-[11px] text-amber-600 dark:text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-md p-2 mb-4 flex items-start gap-1.5">
          <Warning size={14} className="shrink-0 mt-0.5" />
          <span>{t("Guarda tu nueva contraseña en un lugar seguro. No podrás recuperarla sin ayuda del administrador.", "Store your new password safely. You cannot recover it without admin assistance.")}</span>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-[11px] text-muted-foreground block mb-1 flex items-center gap-1">
              <LockKey size={11} /> {t("Contraseña actual (temporal)", "Current password (temporary)")}
            </label>
            <Input
              type="password"
              value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)}
              disabled={saving}
              autoFocus
              data-testid="force-pw-current"
              placeholder="••••••••"
            />
          </div>
          <div>
            <label className="text-[11px] text-muted-foreground block mb-1 flex items-center gap-1">
              <LockKey size={11} /> {t("Nueva contraseña (mín. 8)", "New password (min 8)")}
            </label>
            <Input
              type="password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              disabled={saving}
              data-testid="force-pw-new"
              placeholder="••••••••"
              minLength={8}
            />
          </div>
          <div>
            <label className="text-[11px] text-muted-foreground block mb-1 flex items-center gap-1">
              <LockKey size={11} /> {t("Confirmar nueva contraseña", "Confirm new password")}
            </label>
            <Input
              type="password"
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              disabled={saving}
              data-testid="force-pw-confirm"
              placeholder="••••••••"
            />
          </div>
        </div>

        <Button
          className="w-full mt-5"
          onClick={submit}
          disabled={saving || !currentPw || !newPw || !confirmPw}
          data-testid="force-pw-submit"
        >
          {saving && <CircleNotch size={14} className="animate-spin mr-2" />}
          {t("Establecer nueva contraseña", "Set new password")}
        </Button>
      </div>
    </div>
  );
}
